<template>
  <div class="mt-3">
    <!-- ----------------------------------------------------- -->
    <!-- main headings -->
    <!-- ----------------------------------------------------- -->
    <div class="mt-3">
      <div class="row">
        <p class="bb-sheet-title">CHARACTERISTIC DETAILS</p>
      </div>
    </div>
    <div
      v-if="isReady"
      class="container-fluid mt-3 mb-0"
    >
      <!-- ----------------------------------------------------- -->
      <!-- table headings -->
      <!-- ----------------------------------------------------- -->
      <div class="row">
        <div class="bb-col-char-compact text-center">
          <p class="bb-hdg mt-2 mb-3">Current</p>
        </div>
        <div class="bb-col-char-compact text-center">
          <p class="bb-hdg mt-2 mb-3">Max</p>
        </div>
        <div class="bb-col-counter text-center">
          <p class="bb-hdg mt-2 mb-3">Rolled</p>
        </div>
        <div class="bb-col-char-compact text-center">
          <p class="bb-hdg mt-2 mb-3">Integs</p>
        </div>
        <div class="bb-col-counter text-center">
          <p class="bb-hdg mt-2 mb-3">Improvs</p>
        </div>
        <div class="bb-col-counter text-center">
          <p class="bb-hdg mt-2 mb-3">Loss</p>
        </div>
        <div class="bb-col-char-compact text-center">
          <p class="bb-hdg mt-2 mb-3">Adjs</p>
        </div>
        <div class="col-3 text-center">
          <p class="bb-hdg mt-2 mb-3">Reasons</p>
        </div>
        <div class="bb-col-char-compact text-center">
          <p class="bb-hdg mt-2 mb-3">Increases</p>
        </div>
        <div class="col-auto ms-3">
          <p class="bb-hdg mt-2 mb-3">&nbsp;</p>
        </div>
      </div>
      <!-- ----------------------------------------------------- -->
      <!-- Data Rows -->
      <!-- ----------------------------------------------------- -->
      <div
        :class="[
          'row mt-3 align-items-center bb-tooltip-wrapper',
          char === 'cha' ? 'mb-0' : 'mb-3',
        ]"
        v-for="char in ['str', 'con', 'dex', 'siz', 'int', 'pow', 'cha']"
        :key="char"
      >
        <!-- ----------------------------------------------------- -->
        <!-- Current -->
        <!-- ----------------------------------------------------- -->
        <div class="bb-col-char-compact text-center">
          <ToolTip
            :text="displayCurrentText(char)"
            position="bottom"
            :tableWrapperMode="true"
            maxWidth="420px"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :class="{ 'bb-warning': isCurMax(char) }"
                  :value="character[`${char}_cur`]"
                  :id="`${char}_cur`"
                  disabled
                />
                <label
                  class="readonly bb-float-label optional"
                  :class="{ 'bb-warning': isCurMax(char) }"
                  :for="`${char}_cur`"
                >
                  {{ charLabels[char] }}
                  <span class="bb-orange-circle"></span>
                </label>
              </div>
            </div>
          </ToolTip>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Max -->
        <!-- ----------------------------------------------------- -->
        <div class="bb-col-char-compact text-center">
          <ToolTip
            :text="displayMaxText(char)"
            position="bottom"
            :tableWrapperMode="true"
            maxWidth="420px"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="character[`${char}_max`]"
                  :id="`${char}_max`"
                  disabled
                />
                <label
                  class="readonly bb-float-label optional"
                  :for="`${char}_max`"
                >
                  {{ charLabels[char] }}
                  <span class="bb-orange-circle"></span>
                </label>
              </div>
            </div>
          </ToolTip>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Rolled -->
        <!-- ----------------------------------------------------- -->
        <div class="bb-col-counter text-center">
          <CounterComponent
            :pass_ref="`counter-${char}-rolled`"
            :pass_label="charLabels[char]"
            :pass_count="local_character[`${char}_rolled`]"
            :pass_min="local_character[`${char}_min`]"
            :pass_max="getRolledMax(char)"
            :pass_digits="char_digits"
          />
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Integ -->
        <!-- ----------------------------------------------------- -->
        <div class="bb-col-char-compact text-center">
          <div class="form-group">
            <div
              class="form-floating"
              v-if="char === 'pow'"
            >
              <input
                readonly
                class="form-control text-center"
                :value="local_character.pow_integs_tot"
                id="pow_integs_tot"
                disabled
              />
              <label
                class="readonly bb-float-label optional"
                for="pow_integs_tot"
                >{{ charLabels['pow'] }}</label
              >
            </div>
            <div
              class="form-floating"
              v-else
              style="visibility: hidden"
            >
              <input
                readonly
                class="form-control text-center"
                disabled
              />
            </div>
          </div>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Improv -->
        <!-- ----------------------------------------------------- -->
        <div class="bb-col-counter text-center">
          <CounterComponent
            :pass_ref="`counter-${char}-improvs-tot`"
            :pass_label="charLabels[char]"
            :pass_count="local_character[`${char}_improvs_tot`]"
            :pass_min="min_improvs_tot"
            :pass_max="getImprovMax(char)"
            :pass_digits="char_digits"
            :pass_optional="true"
          />
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Loss / Wounds (CON) -->
        <!-- ----------------------------------------------------- -->
        <div class="bb-col-counter text-center">
          <div v-if="char === 'con'">
            <div
              v-if="isNewCharacter"
              class="form-group"
            >
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="local_character[`${char}_wounds_tot`]"
                  :id="`${char}_wounds_tot`"
                  disabled
                />
                <label
                  class="readonly bb-float-label optional"
                  :for="`${char}_wounds_tot`"
                >
                  {{ charLabels[char] }}
                </label>
              </div>
            </div>
            <CounterComponent
              v-else
              :pass_ref="`counter-${char}-wounds-tot`"
              :pass_label="charLabels[char]"
              :pass_count="local_character[`${char}_wounds_tot`]"
              :pass_min="0"
              :pass_max="nonWoundCon"
              :pass_digits="char_digits"
              :pass_optional="true"
            />
          </div>
          <!-- non CON chars - blank -->
          <div
            v-else
            class="form-floating"
            style="visibility: hidden"
          >
            <input
              readonly
              class="form-control text-center"
              disabled
            />
          </div>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Adj -->
        <!-- ----------------------------------------------------- -->
        <div class="bb-col-char-compact text-center">
          <div class="form-group">
            <div class="form-floating">
              <input
                readonly
                class="form-control text-center"
                :value="adjsTotDisplay(char)"
                :id="`${char}_adjs_tot`"
                disabled
              />
              <label
                class="readonly bb-float-label optional"
                :for="`${char}_adjs_tot`"
              >
                {{ charLabels[char] }}
              </label>
            </div>
          </div>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Adjustment Reason (tooltip on every row) -->
        <!-- ----------------------------------------------------- -->
        <div class="col-3 text-center">
          <ToolTip
            :text="displayAdjReasonsText(char)"
            position="top"
            style="display: block"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control"
                  :value="character[`${char}_adjs_reasons`]"
                  :id="`${char}_adjs_tot`"
                  disabled
                />
                <label
                  class="readonly bb-float-label optional"
                  :for="`${char}_adjs_reasons`"
                >
                  {{ charLabels[char] }} Adj Reasons
                  <span
                    v-if="computedReasons(char)"
                    class="bb-orange-circle"
                  ></span>
                </label>
              </div>
            </div>
          </ToolTip>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Increase -->
        <!-- ----------------------------------------------------- -->
        <div class="bb-col-char-compact text-center">
          <ToolTip
            :text="displayIncreaseText(char)"
            position="bottom"
            :tableWrapperMode="true"
            maxWidth="520px"
          >
            <div class="form-floating">
              <input
                readonly
                class="form-control text-center"
                :value="character[`${char}_incr_percent`]"
                :id="`${char}_incr`"
                disabled
              />
              <label
                class="bb-float-label optional"
                :for="`${char}_incr`"
                >%age
                <span class="bb-orange-circle"></span>
              </label>
            </div>
          </ToolTip>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- Adjustment Button -->
        <!-- ----------------------------------------------------- -->
        <div class="col-auto">
          <button
            :id="char"
            type="button"
            class="btn btn-primary btn-xl btn-circle d-flex justify-content-center align-items-center"
            @click="goOpenAdjs(char)"
          >
            <ToolTip
              :text="`${getCharLabel(char)} Adjustments`"
              position="left"
            >
              <img
                :src="adjustImg"
                height="48"
                width="48"
                alt="adjustments image"
              />
            </ToolTip>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CounterComponent from './Counter.vue'
import ToolTip from '../components/ToolTip.vue'

import adjustImg from '../assets/images/bbAdjust.png'

import copyObjectNoRef from '../helpers/copyObjectNoRef'
import calcImprovMax from '../helpers/calcImprovMax'
import calcRolledMax from '../helpers/calcRolledMax'
import isCurCharMax from '../helpers/isCurCharMax'

import { inject } from 'vue'

export default {
  name: 'CharacterSheetCharacteristics',
  emits: ['update', 'adjust'],
  //========================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
    isNewCharacter: {
      type: Boolean,
      required: false,
      default: false,
    },
    currentRace: {
      type: Object,
      required: false,
      default: null,
    },
  },
  //========================================================
  // components
  //------------
  components: {
    CounterComponent,
    ToolTip,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // watch
  //-------
  watch: {
    //-------------------------------------------------------------
    // watch character so updating chars updates table immediately
    //-------------------------------------------------------------
    character: {
      handler(newChar) {
        if (!newChar) return
        // reload local_character for new/updated character
        this.local_character = copyObjectNoRef(this.character)
      },
      deep: true,
      immediate: true,
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      adjustImg,
      // local_character: JSON.parse(JSON.stringify(this.character)),
      local_character: null,
      isInitialLoad: true,
      charIsMaxed:
        'Current characteristic is at or exceeds maximum value through adjs. Cannot be increased further',
      // characteristic labels as an object for easy access
      charLabels: {
        str: 'Str',
        con: 'Con',
        dex: 'Dex',
        siz: 'Siz',
        int: 'Int',
        pow: 'Pow',
        cha: 'Cha',
      },
      // cr
      cr_label: 'CR',
      cr_min: 0,
      cr_max: 0,
      cr_digits: 1,
      // sr
      sr_label: 'SR',
      sr_min: 0,
      sr_max: 0,
      sr_digits: 2,
      //
      char_digits: 2,
      min_improvs_tot: 0,
      //
      move_min: 0,
      move_max: 0,
      move_fly_min: 0,
      move_fly_max: 0,
      move_digits: 2,
      //
      move_label: 'Move',
      move_fly_label: 'Fly',
      //
      // listeners for counters
      counterRolledListeners: [],
      counterImprovsTotListeners: [],
    }
  },
  //========================================================
  // computed
  //-----------
  computed: {
    isReady() {
      if (this.local_character != null) {
        return true
      }
      return false
    },
    nonWoundCon() {
      if (!this.character) return 0
      const con_cur = this.character['con_cur'] ?? 0
      const con_wounds_tot = this.character['con_wounds_tot'] ?? 0
      return Math.max(con_cur + con_wounds_tot, 0)
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    displayCurrentText(char) {
      if (!this.character || !char) return ''

      const rolled = Number(this.character[`${char}_rolled`] ?? 0)
      const improvsTot = Number(this.character[`${char}_improvs_tot`] ?? 0)
      const adjsTot = Number(this.character[`${char}_adjs_tot`] ?? 0)

      if (char === 'con') {
        const loss = Number(this.character.con_wounds_tot ?? 0)
        return `Cur Con = Rolled (${rolled}) + Improvs (${improvsTot}) + Adjs (${adjsTot}) - Loss (${loss})`
      }

      if (char === 'pow') {
        const integs = Number(this.character.pow_integs_tot ?? 0)
        return `Cur Pow = Rolled (${rolled}) + Improvs (${improvsTot}) + Adjs (${adjsTot}) - Integs (${integs})`
      }

      return `Cur ${this.getCharLabel(char)} = Rolled (${rolled}) + Improvs (${improvsTot}) + Adjs (${adjsTot})`
    },
    displayMaxText(char) {
      if (!char) return ''

      return `Max ${this.getCharLabel(char)} = ${this.getRaceCharacteristicDiceRoll(char)}`
    },
    getRaceCharacteristicDiceRoll(char) {
      if (!char || !this.currentRace) return 'Unknown'

      const flattenedValue = this.currentRace[`${char}_dice_roll`]
      if (
        flattenedValue !== undefined &&
        flattenedValue !== null &&
        `${flattenedValue}` !== ''
      ) {
        return `${flattenedValue}`
      }

      const raceCharacteristics = Array.isArray(
        this.currentRace.characteristics,
      )
        ? this.currentRace.characteristics
        : []
      const characteristic = raceCharacteristics.find(
        (entry) => entry?.name === char,
      )
      const diceRoll = characteristic?.dice_roll

      if (diceRoll !== undefined && diceRoll !== null && `${diceRoll}` !== '') {
        return `${diceRoll}`
      }

      return 'Unknown'
    },
    displayIncreaseText(char) {
      //----------------
      // check got char
      //----------------
      if (!char) return ''
      //------------------------
      // check if char is maxed
      //------------------------
      if (this.isCurMax(char)) {
        return this.charIsMaxed
      }
      //-------------------------------
      // else build increase breakdown
      //-------------------------------
      let rolled = this.character[`${char}_rolled`] ?? 0
      let improvs_tot = this.character[`${char}_improvs_tot`] ?? 0
      let max = this.character[`${char}_max`] ?? 0
      let cur_no_adjs = Number(rolled + improvs_tot)

      let breakdown = `Cur No Adjs [ Rolled (${rolled}) + Improvs Tot (${improvs_tot}) ]`
      breakdown += `\n/ Max (${max}) * 100 + 1`
      breakdown += `\n- Cur No Adjs (${cur_no_adjs})`
      return breakdown
    },
    isCurMax(char) {
      if (!this.character) return false
      let parm = {
        max: this.character[`${char}_max`] ?? 0,
        cur: this.character[`${char}_cur`] ?? 0,
        improvs_tot: this.character[`${char}_improvs_tot`] ?? 0,
      }
      return isCurCharMax(parm)
    },
    getRolledMax(char) {
      if (!this.character) return 0
      let parm = {
        rolled: this.character[`${char}_rolled`] ?? 0,
        improvs_tot: this.character[`${char}_improvs_tot`] ?? 0,
        max: this.character[`${char}_max`] ?? 0,
      }
      return calcRolledMax(parm)
    },
    getImprovMax(char) {
      if (!this.character) return 0
      let parm = {
        rolled: this.character[`${char}_rolled`] ?? 0,
        improvs_tot: this.character[`${char}_improvs_tot`] ?? 0,
        max: this.character[`${char}_max`] ?? 0,
      }
      return calcImprovMax(parm)
    },
    adjsTotDisplay(char) {
      if (!this.character) return ''
      const val = this.character[`${char}_adjs_tot`] ?? 0
      return val > 0 ? `+${val}` : `${val}`
    },
    displayAdjReasonsText(char) {
      if (!this.character || !char || typeof char !== 'string') return ''
      const val = this.character[`${char}_adjs_reasons`]
      if (typeof val === 'string' && val.trim() !== '') {
        return val.trim()
      }
      return 'No adjustment reasons defined for this characteristic'
    },
    computedReasons(char) {
      if (!this.character || !char || typeof char !== 'string') return false
      const val = this.character[`${char}_adjs_reasons`]
      return typeof val === 'string' && val.trim() !== ''
    },
    getTextSizeClass(text) {
      if (!text) return 'text-normal'

      const length = text.length
      if (length > 100) return 'text-xs'
      if (length > 70) return 'text-sm'
      if (length > 40) return 'text-md'
      return 'text-normal'
    },
    getCharLabel(char) {
      return this.charLabels[char] || char.toUpperCase()
    },
    goOpenAdjs(char) {
      console.log('>>>> emitting adjust for char ', char)
      this.$emit('adjust', {
        adj_category: 'characteristic',
        adj_type: char,
      })
    },
    unregisterAllListeners() {
      //--------------------------------
      // clear rolled counter listeners
      //--------------------------------
      Object.entries(this.charLabels).forEach(([key, val]) => {
        this.emitter.off(
          `counter-${key}-rolled`,
          this[`counter${val}RolledListener`],
        )
      })
      //-------------------------------------
      // clear improvs counter tot listeners
      //-------------------------------------
      Object.entries(this.charLabels).forEach(([key, val]) => {
        this.emitter.off(
          `counter-${key}-improvs-tot`,
          this[`counter${val}ImprovsTotListener`],
        )
      })
      if (this.counterConWoundsTotListener) {
        this.emitter.off(
          'counter-con-wounds-tot',
          this.counterConWoundsTotListener,
        )
      }
      this.counterRolledListeners = []
      this.counterImprovsTotListeners = []
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    //-----------------------
    // load passed character
    //-----------------------
    console.log('>>>> mounted CharacterSheetCharacteristics')
    console.log(this.character)
    // this.local_character = copyObjectNoRef(this.character)
    //---------------------------------
    // create rolled counter listeners
    //---------------------------------
    Object.entries(this.charLabels).forEach(([key, val]) => {
      this[`counter${val}RolledListener`] = (return_count) => {
        this.local_character[`${key}_rolled`] = return_count
        this.$emit('update', {
          type: 'characteristic',
          key: `${key}_rolled`,
          value: return_count,
        })
      }
      this.counterRolledListeners.push(this[`counter${val}RolledListener`])
    })
    //--------------------------------------
    // create improvs tot counter listeners
    //--------------------------------------
    Object.entries(this.charLabels).forEach(([key, val]) => {
      this[`counter${val}ImprovsTotListener`] = (return_count) => {
        this.local_character[`${key}_improvs_tot`] = return_count
        this.$emit('update', {
          type: 'characteristic',
          key: `${key}_improvs_tot`,
          value: return_count,
        })
      }
      this.counterImprovsTotListeners.push(
        this[`counter${val}ImprovsTotListener`],
      )
    })
    this.counterConWoundsTotListener = (return_count) => {
      const nextWounds = Number(return_count)
      if (Number.isNaN(nextWounds)) {
        return
      }

      this.local_character.con_wounds_tot = nextWounds
      this.local_character.wounds = nextWounds
      this.$emit('update', {
        type: 'profile',
        key: 'wounds',
        value: nextWounds,
      })
    }
    //-----------------------------------------------------------
    // unregister listeners before registering (belt and braces)
    //-----------------------------------------------------------
    this.unregisterAllListeners()
    //-----------------------------------
    // register rolled counter listeners
    //-----------------------------------
    Object.entries(this.charLabels).forEach(([key, val]) => {
      // console.log(`Registering listener this[counter${val}RolledListener] for counter-${key}-rolled`)
      this.emitter.on(
        `counter-${key}-rolled`,
        this[`counter${val}RolledListener`],
      )
    })
    //----------------------------------------
    // register improvs tot counter listeners
    //----------------------------------------
    Object.entries(this.charLabels).forEach(([key, val]) => {
      // console.log(`Registering listener this[counter${val}ImprovsTotListener] for counter-${key}-improvs-tot`)
      this.emitter.on(
        `counter-${key}-improvs-tot`,
        this[`counter${val}ImprovsTotListener`],
      )
    })
    this.emitter.on('counter-con-wounds-tot', this.counterConWoundsTotListener)
  },
  //========================================================
  // beforeUnmount
  //---------
  beforeUnmount() {
    //--------------------------
    // unregister all listeners
    //--------------------------
    this.unregisterAllListeners()
  },
}
</script>

<style scoped>
.form-group {
  margin-bottom: 0;
}

.bb-col-char-compact {
  flex: 0 0 7.5%;
  max-width: 7.5%;
}

@media (max-width: 1200px) {
  .bb-col-char-compact {
    flex: 0 0 9%;
    max-width: 9%;
  }
}
</style>
